import { formatMapName } from '../../liveSession/lib/formatters';
import type { PlaybookSummary, StrategyDetail, StrategyLearningResponse } from '../types';

export type KnowledgeFilter = 'all' | 'known' | 'pending';
export type SortKey = 'updated' | 'name' | 'progress';

export type PlaybookQuery = {
  q: string;
  map: string;
  type: string;
  side: 'all' | 'T' | 'CT';
  knowledge: KnowledgeFilter;
  sort: SortKey;
};

export const EMPTY_QUERY: PlaybookQuery = {
  q: '',
  map: 'all',
  type: 'all',
  side: 'all',
  knowledge: 'all',
  sort: 'updated',
};

export function mapKey(map: string | null | undefined): string {
  return (map ?? '').trim().toLowerCase().replace(/^de_/, '');
}

export function isKnown(strategy: { roleCount: number; rolesLearned: number }): boolean {
  return strategy.roleCount > 0 && strategy.rolesLearned === strategy.roleCount;
}

export function playbookStats(strategies: PlaybookSummary[]) {
  const known = strategies.filter(isKnown).length;
  return {
    total: strategies.length,
    known,
    pending: strategies.length - known,
    mapCount: new Set(strategies.map((strategy) => mapKey(strategy.map)).filter(Boolean)).size,
  };
}

export function isQueryFiltered(query: PlaybookQuery): boolean {
  return Boolean(query.q.trim())
    || query.map !== 'all'
    || query.type !== 'all'
    || query.side !== 'all'
    || query.knowledge !== 'all';
}

export function parseListSearch(params: URLSearchParams): PlaybookQuery {
  const sort = params.get('sort');
  const knowledge = params.get('knowledge');
  const side = (params.get('side') ?? '').trim().toUpperCase();
  return {
    q: params.get('q') ?? '',
    map: params.get('map') || 'all',
    type: (params.get('type') || 'all').toLowerCase(),
    side: side === 'T' || side === 'CT' ? side : 'all',
    knowledge: knowledge === 'known' || knowledge === 'pending' ? knowledge : 'all',
    sort: sort === 'name' || sort === 'progress' ? sort : 'updated',
  };
}

export function listSearchString(query: PlaybookQuery): string {
  const params = new URLSearchParams();
  if (query.q.trim()) params.set('q', query.q);
  if (query.map !== 'all') params.set('map', query.map);
  if (query.type !== 'all') params.set('type', query.type);
  if (query.side !== 'all') params.set('side', query.side);
  if (query.knowledge !== 'all') params.set('knowledge', query.knowledge);
  if (query.sort !== 'updated') params.set('sort', query.sort);
  return params.toString();
}

export function filterPlaybook(strategies: PlaybookSummary[], query: PlaybookQuery): PlaybookSummary[] {
  const needle = query.q.trim().toLowerCase();
  const filtered = strategies.filter((strategy) => {
    if (needle) {
      const haystack = [
        strategy.name,
        strategy.id,
        strategy.description,
        strategy.map,
        formatMapName(strategy.map),
        strategy.route,
        strategy.site,
      ]
        .filter((part): part is string => Boolean(part))
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (query.map !== 'all' && mapKey(strategy.map) !== query.map) return false;
    if (query.type !== 'all' && (strategy.strategyType ?? '').toLowerCase() !== query.type) return false;
    if (query.side !== 'all' && strategy.side.trim().toUpperCase() !== query.side) return false;
    if (query.knowledge === 'known' && !isKnown(strategy)) return false;
    if (query.knowledge === 'pending' && isKnown(strategy)) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    if (query.sort === 'name') return a.name.localeCompare(b.name);
    if (query.sort === 'progress') {
      return b.rolesLearned - a.rolesLearned || a.name.localeCompare(b.name);
    }
    return b.updatedAt.localeCompare(a.updatedAt) || a.name.localeCompare(b.name);
  });
}

export function mapOptions(strategies: PlaybookSummary[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const strategy of strategies) {
    const key = mapKey(strategy.map);
    if (!key || seen.has(key)) continue;
    seen.set(key, formatMapName(strategy.map) || strategy.map);
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

const TYPE_ORDER = ['execute', 'default', 'rush', 'retake', 'hold'];

export function typeOptions(strategies: PlaybookSummary[]): string[] {
  const present = new Set(
    strategies
      .map((strategy) => (strategy.strategyType ?? '').trim().toLowerCase())
      .filter(Boolean),
  );
  const known = TYPE_ORDER.filter((type) => present.has(type));
  const extra = [...present].filter((type) => !TYPE_ORDER.includes(type)).sort();
  return [...known, ...extra];
}

export function applyLearning(detail: StrategyDetail, learning: StrategyLearningResponse): StrategyDetail {
  const learned = new Map(learning.roles.map((role) => [role.id, role.learned]));
  return {
    ...detail,
    roles: detail.roles.map((role) => {
      const marked = learned.get(role.id);
      return { ...role, learned: marked === undefined ? role.learned : marked };
    }),
  };
}
